import { Button, Grid, ProgressBar } from '@design-systems/canvas-ui'
import { useState } from 'react'
import Icon, {
    CartIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CardCreditIcon,
} from '@design-systems/react-icons'
import { Container } from 'components/container/Container'
import { useSalesOverview } from 'hooks/sales/useSalesOverview'
import { Link, useLocation } from 'react-router-dom'
import {
    getDynamicSubPath,
    getLocationFilter,
    getPercentClass,
    getPercentComplete,
    getRealPercent,
} from '../helpers'
import { BasicPlaceholder } from 'components/placeholder'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { ErrorAlert } from 'components/error-fallback'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { format } from 'date-fns'
import { TimePeriods } from 'components/timeperiods'
import { useLogClick } from 'utils/analytics'
import './overview.scss'
import BasketHistorical from './basketHistorical'
import { formatPercent } from 'utils/number-format'
import { formatCurrency, formatTransactions } from 'utils/currency-helper'

export const SalesOverview = () => {
    const { sales } = TRANSLATION_KEYS
    const { overview } = sales
    const timePeriodsKeys = sales.timePeriods
    const { currentLocation, env, t, logout, session } = useEssentials()
    const queryParams = useLocation().search
    const { logClick } = useLogClick('sales overview')

    const locationFilter = getLocationFilter(currentLocation)
    const currentDate = new Date()
    const filters = {
        date: format(currentDate, 'yyyy-MM-dd'),
        source: 'greenfield',
        ...locationFilter,
    }

    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)

    const {
        data,
        isLoading,
        isError: queryIsError,
        error,
        refetch,
        remove,
    } = useSalesOverview({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        dynamicSubPath: getDynamicSubPath(currentLocation),
        filters,
        timePeriodIndex,
    })

    // Only enable pull-to-refresh for "today" time period (index 0)
    useRefresh(refetch, remove, timePeriodIndex === 0)
    const count = useCountdown(5, logout, error as Error)

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={10} columns={3} />
            </div>
        )
    }

    const hasEmptyDataResponse =
        !isLoading && !queryIsError && Array.isArray(data) && data.length === 0

    if (hasEmptyDataResponse) {
        return (
            <ErrorAlert
                error={new Error(t(TRANSLATION_KEYS.sales.noPermission))}
                variant="custom"
            />
        )
    }

    if (queryIsError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const timePeriods = [
        t(timePeriodsKeys.today),
        t(timePeriodsKeys.yesterday),
        t(timePeriodsKeys.weekToDate),
        t(timePeriodsKeys.lastWeek),
        t(timePeriodsKeys.monthToDate),
        t(timePeriodsKeys.lastMonth),
        t(timePeriodsKeys.yearToDate),
    ]

    const currentDataSet = data?.[0]
    if (!currentDataSet) {
        return (
            <ErrorAlert
                error={new Error(t(TRANSLATION_KEYS.alert.noData.heading))}
                count={count}
            />
        )
    }

    const uniqueKey: string =
        Object.keys(currentDataSet).find(
            (key) =>
                ![
                    'id',
                    'location_id',
                    'date',
                    'sales_by_location',
                    'region_id',
                    'group_id',
                    'district_id',
                    'store_id',
                ].includes(key),
        ) || ''
    const currentData = currentDataSet[uniqueKey]

    const currentTimePeriodKey = timePeriods[timePeriodIndex]
    const currentTimePeriod = timePeriods[timePeriodIndex]
    const realGoalPercent = getRealPercent(
        currentData.forecast_pct,
        currentData.forecast_status,
    )
    const realGoalPercentFormatted = formatPercent(realGoalPercent)
    const salesFormatted = formatCurrency(currentData.sales)
    const todayLink = (
        <Button
            className="hc8-ma-none hc8-pa-none"
            aria-label={t(overview.hourlySales)}
            onClick={() => logClick('hourly sales')}
            variant="ghost"
        >
            <Link
                to={`/sales/hourly${queryParams}`}
                className="hc8-fs-200 sales-overview-hourly-link"
            >
                {salesFormatted}
            </Link>
        </Button>
    )
    const todaySalesElement =
        currentTimePeriod === t(timePeriodsKeys.today)
            ? todayLink
            : salesFormatted

    const isNonIntraDay = currentTimePeriodKey !== t(timePeriodsKeys.today)
    const percentComplete = getPercentComplete(realGoalPercent)

    const handleTabSelect = (timePeriod: string, index: number) => {
        setTimePeriodIndex(index)
    }

    return (
        <>
            <TimePeriods
                timePeriods={timePeriods}
                activeTimePeriod={timePeriods[timePeriodIndex]}
                onTabSelect={handleTabSelect}
                page="sales overview"
                isTimePeriodSticky={false}
            />
            <Container role="main" className="hc8-ph-4x">
                <Grid.Container
                    align="center"
                    justify="space-around"
                    className="hc8-pa-6x"
                >
                    <Grid.Item
                        size={{
                            xs: 9,
                        }}
                    >
                        <span id="sales-info" className="hc8-sr-only">
                            {salesFormatted} {t(overview.of)}{' '}
                            {formatCurrency(currentData.forecast)}
                            {t(overview.goalPercentage)}
                            {realGoalPercentFormatted}
                        </span>
                        <span className="hc8-fs-200">{todaySalesElement}</span>
                        <span aria-hidden="true">
                            {' '}
                            {t(overview.of) + ' '}
                            {formatCurrency(currentData.forecast)}{' '}
                            <span className={getPercentClass(realGoalPercent)}>
                                {realGoalPercentFormatted}
                            </span>
                        </span>
                        <ProgressBar
                            percentComplete={percentComplete}
                            className={
                                isNonIntraDay && percentComplete < 100
                                    ? 'incomplete'
                                    : ''
                            }
                        />
                    </Grid.Item>
                </Grid.Container>
                <SalesOverviewDataRow
                    label={t(overview.originated)}
                    dollars={currentData.sales_breakdown.originated}
                    percent={currentData.sales_breakdown.originated_pct}
                    isChild={false}
                />
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.lineBusting)}
                        dollars={currentData.sales_breakdown.line_bust}
                        percent={currentData.sales_breakdown.line_bust_pct}
                    />
                )}
                <SalesOverviewDataRow
                    label={t(overview.fulfilled)}
                    dollars={currentData.fulfilled_sales}
                    percent={currentData.fulfilled_pct}
                    isChild={false}
                />
                <SalesOverviewDataRow
                    label={t(overview.shipFromStore)}
                    dollars={currentData.sales_breakdown.ship_from_store}
                    percent={currentData.sales_breakdown.ship_from_store_pct}
                />
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.driveUp)}
                        dollars={currentData.sales_breakdown.drive_up}
                        percent={currentData.sales_breakdown.drive_up_pct}
                    />
                )}
                <SalesOverviewDataRow
                    label={t(overview.orderPickup)}
                    dollars={currentData.sales_breakdown.order_pickup}
                    percent={currentData.sales_breakdown.order_pickup_pct}
                />
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.shipt)}
                        dollars={currentData.sales_breakdown.shipt}
                        percent={currentData.sales_breakdown.shipt_pct}
                    />
                )}
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.otherSales)}
                        isChild={false}
                        showData={false}
                    />
                )}
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.saveTheSale)}
                        dollars={currentData.sales_breakdown.mycheckout}
                        percent={currentData.sales_breakdown.mycheckout_pct}
                    />
                )}
                <SalesOverviewDataRow
                    label={t(overview.lastYear)}
                    isChild={false}
                    showData={false}
                />
                <SalesOverviewDataRow
                    label={t(overview.compPercent)}
                    dollars={
                        currentTimePeriod === 'today'
                            ? currentData.last_year_total_sales
                            : currentData.last_year_sales
                    }
                    percent={currentData.ly_admapped}
                />
                <SalesOverviewDataRow
                    label={t(overview.originatedCompPercent)}
                    dollars={currentData.last_year_total_sales_originated}
                    percent={currentData.originated_ly_admapped}
                />
                <SalesOverviewDataRow
                    label={t(overview.fulfilledCompPercent)}
                    dollars={currentData.last_year_total_sales_fulfilled}
                    percent={currentData.fulfilled_ly_admapped}
                />
                {isNonIntraDay && (
                    <SalesOverviewDataRow
                        label={t(overview.twoYearCagr)}
                        isChild={false}
                        hideDollars
                        percent={currentDataSet[uniqueKey]?.cagr}
                    />
                )}
                <Grid.Container>
                    <Grid.Item
                        className="hc8-ph-none"
                        size={{
                            xs: 12,
                        }}
                    >
                        <hr />
                    </Grid.Item>
                </Grid.Container>
                <Grid.Container>
                    {!isNonIntraDay && (
                        <>
                            <Grid.Item
                                className="hc8-pl-none hc8-pt-3x"
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Grid.Container className="hc8-pb-3x">
                                    <Grid.Item>
                                        <strong className="heading-with-icon">
                                            <Icon svg={CartIcon} aria-hidden />
                                            {t(overview.basketSize)}
                                        </strong>
                                    </Grid.Item>
                                </Grid.Container>
                                <SalesOverviewSmallwDataRow
                                    label={t(overview.total)}
                                    value={currentData.average_basket_size}
                                    type="currency"
                                />
                                <SalesOverviewSmallwDataRow
                                    label={t(overview.ly)}
                                    value={
                                        currentData.last_year_average_basket_size
                                    }
                                    type="currency"
                                />
                            </Grid.Item>
                            <Grid.Item
                                className="hc8-pl-none hc8-pt-3x"
                                size={{
                                    xs: 6,
                                }}
                            >
                                <Grid.Container className="hc8-pb-3x">
                                    <Grid.Item>
                                        <strong className="heading-with-icon">
                                            <Icon
                                                svg={CardCreditIcon}
                                                aria-hidden
                                            />
                                            {t(overview.transactions)}
                                        </strong>
                                    </Grid.Item>
                                </Grid.Container>
                                <SalesOverviewSmallwDataRow
                                    label={t(overview.total)}
                                    value={currentData.total_transaction_count}
                                    edgePadding={true}
                                    type="number"
                                />
                                <SalesOverviewSmallwDataRow
                                    label={t(overview.ly)}
                                    value={
                                        currentData.last_year_total_transaction_count
                                    }
                                    edgePadding={true}
                                    type="number"
                                />
                            </Grid.Item>
                        </>
                    )}
                </Grid.Container>
                {isNonIntraDay && (
                    <>
                        <Grid.Container className="hc8-pt-6x">
                            <Grid.Item
                                size={{
                                    xs: 12,
                                }}
                            >
                                <BasketHistorical
                                    timePeriodIndex={timePeriodIndex}
                                />
                            </Grid.Item>
                        </Grid.Container>
                    </>
                )}
            </Container>
        </>
    )
}

type SalesOverviewDataRowProps = {
    label: string
    showData?: boolean
    isChild?: boolean
    dollars?: number
    percent?: number
    hideDollars?: boolean
}
const SalesOverviewDataRow = ({
    label,
    showData = true,
    isChild = true,
    dollars,
    percent,
    hideDollars = false,
}: SalesOverviewDataRowProps) => {
    if (!showData) {
        return (
            <Grid.Container className="hc8-pv-3x">
                <Grid.Item
                    className={isChild ? 'hc8-pl-6x' : 'hc8-ph-1x'}
                    size={{
                        xs: 6,
                    }}
                >
                    {isChild ? label : <strong>{label}</strong>}
                </Grid.Item>
            </Grid.Container>
        )
    }
    return (
        <Grid.Container className="hc8-pv-3x">
            <Grid.Item
                className={isChild ? 'hc8-pl-6x' : 'hc8-ph-1x'}
                aria-hidden="true"
                size={{
                    xs: 6,
                }}
            >
                {isChild ? label : <strong>{label}</strong>}
            </Grid.Item>
            {!hideDollars &&
                (dollars ? (
                    <Grid.Item
                        className="hc8-ph-1x hc8-ta-right"
                        aria-hidden="true"
                        size={{
                            xs: 3,
                        }}
                    >
                        {formatCurrency(dollars)}
                    </Grid.Item>
                ) : (
                    <Grid.Item
                        className="hc8-ph-1x hc8-ta-right"
                        aria-hidden="true"
                        size={{
                            xs: 3,
                        }}
                    >
                        {formatCurrency(0)}
                    </Grid.Item>
                ))}
            {hideDollars && (
                <Grid.Item
                    aria-hidden="true"
                    size={{
                        xs: 3,
                    }}
                />
            )}
            {percent ? (
                <Grid.Item
                    className="hc8-ph-1x hc8-ta-right"
                    aria-hidden="true"
                    size={{
                        xs: 3,
                    }}
                >
                    <Grid.Item className={getPercentClass(percent)}>
                        {formatPercent(percent) || '0.00%'}
                    </Grid.Item>
                </Grid.Item>
            ) : (
                <Grid.Item
                    className="hc8-ph-1x hc8-ta-right"
                    aria-hidden="true"
                    size={{
                        xs: 3,
                    }}
                >
                    <Grid.Item>
                        {'0.00'}
                        {'%'}
                    </Grid.Item>
                </Grid.Item>
            )}
            <Grid.Item className="hc8-sr-only">
                {isChild ? label : `Label: ${label}`}
                {dollars ? `, Amount: ${formatCurrency(dollars)}` : ''}
                {percent
                    ? `, Percentage: ${formatPercent(percent) || '0.00%'}`
                    : ', Percentage: 0.00%'}
            </Grid.Item>
        </Grid.Container>
    )
}

type SalesOverviewSmallDataRowProps = {
    label: string
    value: number
    arrow?: string
    edgePadding?: boolean
    type?: 'number' | 'currency' | 'percent'
}
const SalesOverviewSmallwDataRow = ({
    label,
    value,
    arrow,
    edgePadding = false,
    type = 'currency',
}: SalesOverviewSmallDataRowProps) => {
    const icon = arrow === 'up' ? ChevronUpIcon : ChevronDownIcon
    const iconClass = arrow === 'up' ? 'mpm-clr-success' : 'mpm-clr-warning'
    const formattedValue =
        type === 'percent'
            ? formatPercent(value)
            : type === 'currency'
              ? formatCurrency(value)
              : formatTransactions(value)

    return (
        <Grid.Container>
            <Grid.Item
                className="hc8-pr-1x hc8-pb-3x"
                aria-hidden="true"
                size={{
                    xs: 6,
                }}
            >
                {label}
            </Grid.Item>
            <Grid.Item
                className={`hc8-ta-right ${edgePadding ? 'hc8-ph-1x' : 'hc8-pl-1x'}`}
                aria-hidden="true"
                size={{
                    xs: 6,
                }}
            >
                {formattedValue}

                {arrow && (
                    <>
                        <Icon
                            svg={icon}
                            size="sm"
                            className={`${iconClass} hc8-ml-1x`}
                            aria-hidden
                        />
                    </>
                )}
            </Grid.Item>
            <Grid.Item className="hc8-sr-only">
                {`${label}: ${formattedValue}`}
            </Grid.Item>
        </Grid.Container>
    )
}
